import React, { useLayoutEffect, useState } from "react";

/**
 * The rendered width of a box, so an SVG chart can draw in real pixels: a
 * fixed viewBox stretched to a wide card scales its 11px axis text up to ~18px
 * and its bars to a slab (what prod showed on 21 Oct). Falls back to `fallback`
 * where there is no layout (tests, SSR).
 */
function useWidth(fallback = 640) {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    if (!el) return undefined;
    const read = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, width];
}

// Dashboard v2 charts — hand-rolled SVG, like the approved preview. Geometry is
// SVG attributes (no inline styles); colour is a class (`dash-fill--c1` …) so
// both themes come from tokens in dashboard.scss. Series colours cycle c1–c6.

const SERIES = 6;
const seriesClass = (i) => `dash-fill--c${(i % SERIES) + 1}`;

const safe = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// A "nice" axis ceiling: 1 / 2 / 2.5 / 5 / 10 × 10^n.
const nice = (v) => {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
};

/**
 * Vertical bars. With `previous`, each slot shows previous (muted) + current
 * pair; otherwise the LAST bar is highlighted (the current, still-filling period).
 */
export function BarChart({ values = [], labels = [], previous = null, format = (v) => String(v), height = 220, label }) {
  const [box, measured] = useWidth();
  const vals = values.map(safe);
  const prev = previous ? previous.map(safe) : null;
  if (!vals.length) return null;
  const W = Math.max(240, measured);
  const H = height;
  const pl = 52;
  const pr = 6;
  const pt = 16;
  const pb = 26;
  const max = nice(Math.max(...vals, ...(prev || []), 0) * 1.05);
  const cw = (W - pl - pr) / vals.length;
  const y = (v) => pt + (H - pt - pb) * (1 - v / max);
  const every = Math.ceil(vals.length / 12);
  return (
    <div ref={box} className="dash-chart-box">
    <svg className="dash-chart" viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={label}>
      {[0, 0.5, 1].map((t) => (
        <g key={t}>
          <line className="dash-chart__grid" x1={pl} x2={W - pr} y1={y(max * t)} y2={y(max * t)} />
          <text className="dash-chart__axis" x={pl - 8} y={y(max * t) + 4} textAnchor="end">{format(max * t)}</text>
        </g>
      ))}
      {vals.map((v, i) => {
        const x0 = pl + i * cw;
        const last = i === vals.length - 1;
        return (
          <g key={i}>
            {prev ? (
              <>
                <rect className="dash-chart__bar dash-chart__bar--prev" x={x0 + cw / 2 - Math.min(16, cw * 0.3) - 1} y={y(prev[i] ?? 0)} width={Math.min(16, cw * 0.3)} height={Math.max(0, y(0) - y(prev[i] ?? 0))} rx="3">
                  <title>{`Previous: ${format(prev[i] ?? 0)}`}</title>
                </rect>
                <rect className="dash-chart__bar dash-chart__bar--current" x={x0 + cw / 2 + 1} y={y(v)} width={Math.min(16, cw * 0.3)} height={Math.max(0, y(0) - y(v))} rx="3">
                  <title>{`${labels[i] ?? ""}: ${format(v)}`}</title>
                </rect>
              </>
            ) : (
              <>
                <rect
                  className={`dash-chart__bar${last ? " dash-chart__bar--current" : " dash-chart__bar--soft"}`}
                  x={x0 + (cw - Math.min(34, cw * 0.58)) / 2}
                  y={y(v)}
                  width={Math.min(34, cw * 0.58)}
                  height={Math.max(0, y(0) - y(v))}
                  rx="5"
                >
                  <title>{`${labels[i] ?? ""}: ${format(v)}`}</title>
                </rect>
                {last ? (
                  <text className="dash-chart__value" x={x0 + cw / 2} y={Math.max(12, y(v) - 6)} textAnchor="middle">{format(v)}</text>
                ) : null}
              </>
            )}
            {i % every === 0 || last ? (
              <text className="dash-chart__axis" x={x0 + cw / 2} y={H - 8} textAnchor="middle">{labels[i] ?? ""}</text>
            ) : null}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

/** Small trend line for a KPI tile. */
export function Sparkline({ values = [], tone = 1 }) {
  const a = values.map(safe);
  if (a.length < 2) return null;
  const w = 110;
  const h = 34;
  const mn = Math.min(...a);
  const mx = Math.max(...a);
  const r = mx - mn || 1;
  const pts = a.map((v, i) => [2 + (i * (w - 6)) / (a.length - 1), h - 4 - ((v - mn) / r) * (h - 10)]);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  return (
    <svg className={`dash-spark dash-spark--c${tone}`} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path className="dash-spark__area" d={`${d} L${last[0].toFixed(1)} ${h} L2 ${h}Z`} />
      <path className="dash-spark__line" d={d} />
      <circle className="dash-spark__dot" cx={last[0]} cy={last[1]} r="2.8" />
    </svg>
  );
}

/** A 0–100 progress track. `marker` draws a lighter segment up to it (e.g. "incl. today"). */
export function Progress({ value = 0, ahead = null, label }) {
  const v = Math.max(0, Math.min(100, safe(value)));
  const a = ahead == null ? null : Math.max(0, Math.min(100, safe(ahead)));
  return (
    <svg className="dash-meter" viewBox="0 0 100 10" preserveAspectRatio="none" role="progressbar" aria-label={label} aria-valuenow={Math.round(a ?? v)} aria-valuemin={0} aria-valuemax={100}>
      <rect className="dash-meter__track" x="0" y="0" width="100" height="10" />
      {a != null ? <rect className="dash-meter__ahead" x="0" y="0" width={a} height="10" /> : null}
      <rect className="dash-meter__fill" x="0" y="0" width={v} height="10" />
    </svg>
  );
}

/** Ranked horizontal bars: rows of [name, value]. */
export function BarList({ rows = [], format = (v) => String(v), tone = 1, compact = false }) {
  const max = Math.max(1, ...rows.map((r) => safe(r.value)));
  return (
    <div className={`dash-bars${compact ? " dash-bars--compact" : ""}`}>
      {rows.map((r, i) => (
        <div className="dash-bars__row" key={`${r.name}-${i}`}>
          <span className="dash-bars__name" title={r.name}>{r.name}</span>
          <svg className={`dash-bars__track dash-bars__track--c${tone}`} viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true">
            <rect className="dash-bars__bg" x="0" y="0" width="100" height="8" />
            <rect className="dash-bars__fill" x="0" y="0" width={(safe(r.value) / max) * 100} height="8" />
          </svg>
          <span className="dash-bars__value">{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** One stacked bar + legend with each segment's share. rows: [{name, value}]. */
export function StackBar({ rows = [], tall = false }) {
  const total = rows.reduce((s, r) => s + safe(r.value), 0);
  if (!total) return <p className="dash-muted">Nothing to show yet.</p>;
  let acc = 0;
  return (
    <div className="dash-stackbar">
      <svg className={`dash-stackbar__bar${tall ? " dash-stackbar__bar--tall" : ""}`} viewBox="0 0 100 10" preserveAspectRatio="none" role="img" aria-label={rows.map((r) => `${r.name} ${Math.round((safe(r.value) / total) * 100)}%`).join(", ")}>
        {rows.map((r, i) => {
          const w = (safe(r.value) / total) * 100;
          const x = acc;
          acc += w;
          return (
            <rect key={`${r.name}-${i}`} className={seriesClass(i)} x={x} y="0" width={Math.max(0, w - (i < rows.length - 1 ? 0.4 : 0))} height="10">
              <title>{`${r.name}: ${r.value}`}</title>
            </rect>
          );
        })}
      </svg>
      <Legend rows={rows} total={total} />
    </div>
  );
}

export function Legend({ rows = [], total, format }) {
  const t = total ?? rows.reduce((s, r) => s + safe(r.value), 0);
  return (
    <div className="dash-legend">
      {rows.map((r, i) => (
        <div className="dash-legend__item" key={`${r.name}-${i}`}>
          <svg className="dash-legend__sw" viewBox="0 0 10 10" aria-hidden="true"><rect className={seriesClass(i)} width="10" height="10" rx="3" /></svg>
          <span className="dash-legend__name">{r.name}</span>
          <b className="dash-num">{format ? format(r.value) : t ? `${Math.round((safe(r.value) / t) * 100)}%` : "—"}</b>
        </div>
      ))}
    </div>
  );
}

/** Donut with a centre figure. rows: [{name, value}]. */
export function Donut({ rows = [], center, sub, size = 120 }) {
  const total = rows.reduce((s, r) => s + safe(r.value), 0);
  let acc = 0;
  return (
    <div className="dash-donut">
      <div className="dash-donut__chart">
        <svg viewBox="0 0 42 42" width={size} height={size} role="img" aria-label={rows.map((r) => `${r.name} ${total ? Math.round((safe(r.value) / total) * 100) : 0}%`).join(", ")}>
          <circle className="dash-donut__track" cx="21" cy="21" r="15.9155" fill="none" strokeWidth="6" />
          {total
            ? rows.map((r, i) => {
                const share = (safe(r.value) / total) * 100;
                const offset = 25 - acc; // start at 12 o'clock
                acc += share;
                return (
                  <circle
                    key={`${r.name}-${i}`}
                    className={`dash-donut__seg dash-stroke--c${(i % SERIES) + 1}`}
                    cx="21"
                    cy="21"
                    r="15.9155"
                    fill="none"
                    strokeWidth="6"
                    pathLength="100"
                    strokeDasharray={`${share} ${100 - share}`}
                    strokeDashoffset={offset}
                  />
                );
              })
            : null}
        </svg>
        <span className="dash-donut__center">
          <b className="dash-num">{center}</b>
          {sub ? <small>{sub}</small> : null}
        </span>
      </div>
      <Legend rows={rows} total={total} />
    </div>
  );
}

/** A ring gauge (0–100) with a centre label. */
export function Ring({ value = 0, label, sub, size = 92 }) {
  const v = Math.max(0, Math.min(100, safe(value)));
  return (
    <div className="dash-ring">
      <svg viewBox="0 0 42 42" width={size} height={size} role="img" aria-label={`${label ?? ""} ${sub ?? ""}`.trim()}>
        <circle className="dash-ring__track" cx="21" cy="21" r="15.9155" fill="none" strokeWidth="4.5" />
        <circle className="dash-ring__fill" cx="21" cy="21" r="15.9155" fill="none" strokeWidth="4.5" pathLength="100" strokeDasharray={`${v} ${100 - v}`} strokeDashoffset="25" strokeLinecap="round" />
      </svg>
      <span className="dash-ring__center">
        <b className="dash-num">{label}</b>
        {sub ? <small>{sub}</small> : null}
      </span>
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Hour heat map. `rows`: array of 24-int arrays (one per row); `rowLabels` alongside. */
export function Heatmap({ rows = [], rowLabels = null }) {
  const flat = rows.flat().map(safe);
  const max = Math.max(1, ...flat);
  const level = (v) => (v <= 0 ? 0 : Math.min(6, Math.ceil((safe(v) / max) * 6)));
  const labels = rowLabels || (rows.length === 7 ? DAYS : rows.map(() => "24h"));
  return (
    <div className="dash-heat" role="table" aria-label="Inbound messages by hour">
      {rows.map((r, ri) => (
        <div className="dash-heat__row" role="row" key={ri}>
          <span className="dash-heat__dl" role="rowheader">{labels[ri]}</span>
          {Array.from({ length: 24 }).map((_, h) => (
            <span key={h} role="cell" className={`dash-heat__cell is-l${level(r?.[h])}`} title={`${String(h).padStart(2, "0")}:00 · ${safe(r?.[h])}`} />
          ))}
        </div>
      ))}
      <div className="dash-heat__row dash-heat__row--axis" aria-hidden="true">
        <span />
        {Array.from({ length: 24 }).map((_, h) => (
          <span key={h} className="dash-heat__hl">{h % 3 === 0 ? String(h).padStart(2, "0") : ""}</span>
        ))}
      </div>
    </div>
  );
}
